import React, { useState, useRef } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, storage } from "./firebase";
import { useTranslation } from "react-i18next";

export default function Dashboard({ user }) {
  const [amount, setAmount] = useState("");
  const [transactionDate, setTransactionDate] = useState("");
  const [purpose, setPurpose] = useState("");
  const [comments, setComments] = useState("");
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");
  const fileInputRef = useRef();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("Uploading...");
    try {
      if (!files.length) throw new Error("Please select at least one file");

      const fileUrls = [];
      const timestamp = Date.now();
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(`Uploading ${i + 1} of ${files.length}: ${file.name}`);
        const ext = file.name.includes(".") ? "." + file.name.split(".").pop() : "";
        const uniqueName = `${timestamp}_file${i + 1}${ext}`;
        const storageRef = ref(storage, `receipts/${user.uid}/${uniqueName}`);
        await uploadBytes(storageRef, file);
        const fileUrl = await getDownloadURL(storageRef);
        fileUrls.push(fileUrl);
      }

      const [year, month, day] = transactionDate.split("-").map(Number);
      const localDate = new Date(year, month - 1, day);

      await addDoc(collection(db, "receipts"), {
        userId: user.uid,
        userEmail: user.email,
        amount,
        transactionDate: localDate,
        purpose,
        comments,
        fileUrls,
        timestamp: serverTimestamp(),
      });

      setMessage("Receipt(s) uploaded successfully.");
      handleClearFormFields();
      setTimeout(() => {
        handleClearAll();
      }, 2000);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      setUploadProgress("");
    }
  };

  const handleClearFiles = () => {
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClearAll = () => {
    handleClearFormFields();
    setMessage("");
  };

  const handleClearFormFields = () => {
    setAmount("");
    setTransactionDate("");
    setPurpose("");
    setComments("");
    setFiles([]);
    setUploadProgress("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  return (
    <div style={{ maxWidth: 400, margin: "auto", padding: 20 }}>
      <h2>{t("uploadReceipt")}</h2>
      <form onSubmit={handleSubmit} className="upload-form">
        <div className="field-group">
          <label>{t("transactionDate")}:</label>
          <input
            type="date"
            value={transactionDate}
            required
            onChange={(e) => setTransactionDate(e.target.value)}
          />
        </div>

        <div className="field-group">
          <label>{t("amount")}:</label>
          <input
            type="number"
            value={amount}
            required
            placeholder={t("amount")}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <div className="field-group">
          <label>{t("purpose")}:</label>
          <input
            type="text"
            value={purpose}
            required
            placeholder={t("purpose")}
            onChange={(e) => setPurpose(e.target.value)}
          />
        </div>

        <div className="field-group">
          <label>{t("comments")}:</label>
          <textarea
            value={comments}
            placeholder={t("optionalComments")}
            rows={3}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>

        <div className="field-group">
          <label>{t("uploadFiles")}</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            multiple
            required
            onChange={(e) => setFiles(Array.from(e.target.files))}
          />
        </div>

        {files.length > 0 && (
          <ul>
            {files.map((file, index) => (
              <li key={index}>
                <span>{file.name}</span>
                <button type="button" onClick={() => handleRemoveFile(index)}>✕</button>
              </li>
            ))}
          </ul>
        )}

        {uploadProgress && <p className="upload-progress">{uploadProgress}</p>}

        <div className="button-row">
          <button type="submit">{t("submit")}</button>
          <button type="button" onClick={handleClearFiles}>{t("clearFiles")}</button>
          <button type="button" onClick={handleClearAll}>{t("clearAll")}</button>
        </div>
      </form>
      <p>{message}</p>
    </div>
  );
}
